import React from 'react';

import SearchInput from 'components/search_sidebar/sidebar_search';
import FilterButton from 'components/ui/filter_button';
import FilterCheckboxGroup from 'components/search_sidebar/filter_checkbox_group';
import ResponsiveFilterContainer from 'components/search_page/responsive_filter_container';
import CurrentFilters from 'components/search_page/current_filters';
import Tag from 'components/tags/tag';

import type { FilterOption } from './types';

const USAGE_OPTIONS: FilterOption[] = [
  { value: 'used', label: 'Used' },
  { value: 'not_used', label: 'Not used' },
];

interface Props {
  createdByFilter: string[];
  createdByOptions: FilterOption[];
  documentFilter: string[];
  documentOptions: FilterOption[];
  orientationFilter: string[];
  orientationOptions: FilterOption[];
  pageTypeFilter: string[];
  pageTypeOptions: FilterOption[];
  searchText: string;
  usageFilter: string[];
  onCreatedByChange: (values: string[]) => void;
  onDocumentChange: (values: string[]) => void;
  onOrientationChange: (values: string[]) => void;
  onPageTypeChange: (values: string[]) => void;
  onSearchChange: (value: string) => void;
  onSearchClear: () => void;
  onUsageChange: (values: string[]) => void;
}

interface FilterGroupConfig {
  key: string;
  label: string;
  options: FilterOption[];
  selected: string[];
  singleSelection?: boolean;
  onChange: (values: string[]) => void;
}

export default function PageLibraryFilters({
  createdByFilter,
  createdByOptions,
  documentFilter,
  documentOptions,
  orientationFilter,
  orientationOptions,
  pageTypeFilter,
  pageTypeOptions,
  searchText,
  usageFilter,
  onCreatedByChange,
  onDocumentChange,
  onOrientationChange,
  onPageTypeChange,
  onSearchChange,
  onSearchClear,
  onUsageChange,
}: Props) {
  const filterGroups: FilterGroupConfig[] = [
    {
      key: 'pageType',
      label: 'Page Type',
      options: pageTypeOptions,
      selected: pageTypeFilter,
      onChange: onPageTypeChange,
    },
    {
      key: 'document',
      label: 'Used In Document',
      options: documentOptions,
      selected: documentFilter,
      onChange: onDocumentChange,
    },
    {
      key: 'createdBy',
      label: 'Created By',
      options: createdByOptions,
      selected: createdByFilter,
      onChange: onCreatedByChange,
    },
    {
      key: 'orientation',
      label: 'Orientation',
      options: orientationOptions,
      selected: orientationFilter,
      onChange: onOrientationChange,
    },
    {
      key: 'usage',
      label: 'Usage',
      options: USAGE_OPTIONS,
      selected: usageFilter,
      singleSelection: true,
      onChange: onUsageChange,
    },
  ];

  const handleToggle = (group: FilterGroupConfig, value: string) => {
    if (group.selected.includes(value)) {
      group.onChange(group.selected.filter(v => v !== value));
    } else if (group.singleSelection) {
      group.onChange([value]);
    } else {
      group.onChange([...group.selected, value]);
    }
  };

  const handleClearFilters = () => {
    filterGroups.forEach(group => group.onChange([]));
  };

  const selectedValue = (group: FilterGroupConfig) => {
    if (!group.singleSelection) return group.selected;

    return group.selected.length > 0 ? group.selected[0] : null;
  };

  const renderAppliedFilters = () => {
    const tags: React.ReactElement[] = [];

    filterGroups.forEach((group) => {
      group.selected.forEach((value) => {
        const option = group.options.find(o => o.value === value);
        if (!option) return;

        tags.push(
          <Tag
            key={`${group.key}-${value}`}
            name={value}
            removable
            onRemove={() => group.onChange(group.selected.filter(v => v !== value))}
          >
            {group.label}: {option.label}
          </Tag>
        );
      });
    });

    if (tags.length === 0) return null;

    return (
      <div className="mt-3">
        <CurrentFilters raw onClearFilters={handleClearFilters}>
          {tags}
        </CurrentFilters>
      </div>
    );
  };

  return (
    <div className="mb-3">
      <ResponsiveFilterContainer>
        <SearchInput
          placeholder="Search Pages"
          value={searchText}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
          onClear={onSearchClear}
        />

        {filterGroups.map(group => (
          group.options.length > 0 && (
            <FilterButton
              filtersApplied={group.selected.length > 0}
              key={group.key}
              label={group.label}
              name={`${group.key}Filter`}
              popoverBodyClassName="p-3"
            >
              <FilterCheckboxGroup
                options={group.options}
                selected={selectedValue(group)}
                singleSelection={group.singleSelection}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleToggle(group, e.target.value)}
              />
            </FilterButton>
          )
        ))}
      </ResponsiveFilterContainer>

      {renderAppliedFilters()}
    </div>
  );
}
